import { EvaluationScoreLabel, PerformEvaluationScore } from '../../../commonTypes/evaluation/calculate.types';
import { FC } from 'react';
import { ActionFooter } from '../../Common/ActionFooter';
import { Check } from 'lucide-react';
import { TextArea } from '../../Common/TextArea';
import { useHttpPatchTopicScore } from '../../../hooks';

type Props = {
    topicId: string;
    evaluationId: string;
    topicName: string;
    topicDescription: string;
    currentStep: number;
    totalSteps: number;
    selectedScore?: PerformEvaluationScore;
    onSelect: (score: EvaluationScoreLabel) => void;
    scoreGuide?: Record<EvaluationScoreLabel, { description: string }>;
    note: string;
    onNoteChange: (note: string) => void;
    onBack?: () => void;
    onNext: () => void;
    onStepClick: (step: number) => void;
    completedSteps?: number[];
    isNoteRequired?: boolean;
};

const scoreConfig = {
    [EvaluationScoreLabel.NEGATIVE]: {
        className: 'score-card--negative',
        label: 'Negative',
        badgeClass: 'evaluation-status-badge--negative',
    },
    [EvaluationScoreLabel.NEUTRAL]: {
        className: 'score-card--neutral',
        label: 'Neutral',
        badgeClass: 'evaluation-status-badge--neutral',
    },
    [EvaluationScoreLabel.POSITIVE]: {
        className: 'score-card--positive',
        label: 'Positive',
        badgeClass: 'evaluation-status-badge--positive',
    },
};

export const ScoreSelection: FC<Props> = ({
    topicId,
    evaluationId,
    topicName,
    topicDescription,
    currentStep,
    totalSteps,
    selectedScore,
    onSelect,
    scoreGuide,
    note,
    onNoteChange,
    onBack,
    onNext,
    onStepClick,
    completedSteps = [],
    isNoteRequired = false,
}) => {
    const { exec: updateTopicScore } = useHttpPatchTopicScore();
    if (!scoreGuide) return null;

    const isNoteValid = !isNoteRequired || (note && note.trim().length > 0);
    const isScoreSelected = selectedScore?.score !== null && selectedScore?.score !== undefined;
    const isSkipButtonDisabled = selectedScore?.score !== null || note.length > 0;
    const isNextButtonDisabled = !isScoreSelected || !isNoteValid;

    const handleOnNext = async () => {
        await updateTopicScore(topicId, evaluationId, {
            score: selectedScore?.score as EvaluationScoreLabel,
            note,
        });
        onNext();
    };

    const handleSkip = async () => {
        await updateTopicScore(topicId, evaluationId, {
            score: null,
            note: null,
        });
        onNext();
    };

    const renderStepIndicator = () => {
        const steps = Array.from({ length: totalSteps }, (_, i) => i + 1);

        return (
            <div className="step-indicator">
                {steps.map(stepNumber => {
                    const isCompleted = completedSteps.includes(stepNumber);
                    const isCurrent = stepNumber === currentStep;

                    return (
                        <div key={stepNumber} className="step-indicator__step">
                            <div
                                className={`step-indicator__circle step-indicator__circle--clickable ${
                                    isCompleted && 'step-indicator__circle--completed'
                                }
                                        ${isCurrent ? 'step-indicator__circle--current' : 'step-indicator__circle--pending'}`}
                                onClick={() => onStepClick(stepNumber)}
                            >
                                {isCompleted ? <Check className="step-indicator__icon" size={16} /> : <p className="step-indicator__icon">?</p>}
                            </div>
                            {stepNumber < totalSteps && <div className="step-indicator__line" />}
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="evaluation-step">
            <div className="evaluation-step__header">
                {renderStepIndicator()}
                <h1 className="evaluation-step__title">{topicName}</h1>
                <p className="evaluation-step__description">{topicDescription}</p>
            </div>

            <div className="evaluation-step__content">
                <div className="evaluation-step__cards">
                    {[EvaluationScoreLabel.NEGATIVE, EvaluationScoreLabel.NEUTRAL, EvaluationScoreLabel.POSITIVE].map(score => {
                        const isSelected = selectedScore?.score === score;
                        const config = scoreConfig[score];
                        const description = scoreGuide[score]?.description;

                        return (
                            <div
                                key={score}
                                className={`score-card ${config.className} ${isSelected ? 'score-card--selected' : ''}`}
                                onClick={() => onSelect(score)}
                            >
                                <div className={`evaluation-status-badge ${config.badgeClass}`}>{config.label}</div>
                                <div className="score-card__description">{description || 'No description'}</div>
                            </div>
                        );
                    })}
                </div>

                <div className="evaluation-step__notes">
                    <TextArea
                        id="evaluation-note"
                        label="Notes"
                        value={note}
                        onChange={e => onNoteChange(e.target.value)}
                        placeholder="Leave additional comment here..."
                        isRequired={isNoteRequired}
                    />
                </div>
            </div>

            <ActionFooter
                buttons={[
                    {
                        text: 'Back',
                        onClick: onBack || (() => {}),
                        variant: 'text',
                        color: 'blue',
                    },
                    {
                        text: 'Skip',
                        onClick: handleSkip,
                        variant: 'text',
                        color: 'blue',
                        disabled: isSkipButtonDisabled,
                    },
                    {
                        text: 'Next',
                        onClick: handleOnNext,
                        variant: 'contained',
                        disabled: isNextButtonDisabled,
                    },
                ]}
                className="start-evaluation-process__footer"
            />
        </div>
    );
};
